import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, increment, onSnapshot, disableNetwork, enableNetwork } from 'firebase/firestore';
import { db } from './db';
import { IS_CHINA_MODE } from './chinaMode';   // V148 중국 모드: 연결이 끊기면 다시 붙이고 자동으로 다시 불러온다
import {
  CATEGORIES, PRICES,
  INTERIOR_CATEGORIES, INTERIOR_PRICES,
  BEAUTY_CATEGORIES,   BEAUTY_PRICES,
  AUTO_CATEGORIES,     AUTO_PRICES,
  LAUNDRY_CATEGORIES,  LAUNDRY_PRICES,
} from './ddabakData';
import { encryptField, decryptField } from './crypto';
import { getIndustryMode } from './industryConfig';

function getDefaultsByMode(mode) {
  switch (mode) {
    case 'interior': return { defaultCats: INTERIOR_CATEGORIES, defaultPrices: INTERIOR_PRICES };
    case 'beauty':   return { defaultCats: BEAUTY_CATEGORIES,   defaultPrices: BEAUTY_PRICES   };
    case 'auto':     return { defaultCats: AUTO_CATEGORIES,     defaultPrices: AUTO_PRICES     };
    case 'laundry':  return { defaultCats: LAUNDRY_CATEGORIES,  defaultPrices: LAUNDRY_PRICES  };
    default:         return { defaultCats: CATEGORIES,          defaultPrices: PRICES          };
  }
}

const catsField   = (m) => 'cats_'   + m;
const pricesField = (m) => 'prices_' + m;

async function fetchModeData(uid, data, targetMode, industryMode) {
  let rawCats   = data[catsField(targetMode)];
  let rawPrices = data[pricesField(targetMode)];

  if (!rawCats && !rawPrices) {
    if (targetMode === (industryMode || 'food')) {
      rawCats   = data.categories;
      rawPrices = data.prices;
      if (rawCats || rawPrices) {
        const patch = {};
        if (rawCats)   patch[catsField(targetMode)]   = rawCats;
        if (rawPrices) patch[pricesField(targetMode)] = rawPrices;
        await setDoc(doc(db, 'users', uid), patch, { merge: true });
      }
    }
    if (!rawCats && !rawPrices) {
      const { defaultCats, defaultPrices } = getDefaultsByMode(targetMode);
      const enc = await encryptField(uid, defaultCats);
      const patch = {};
      patch[catsField(targetMode)]   = enc;
      patch[pricesField(targetMode)] = defaultPrices;
      await setDoc(doc(db, 'users', uid), patch, { merge: true });
      return { cats: defaultCats, prices: defaultPrices };
    }
  }

  if (typeof rawCats === 'string' && rawCats.startsWith('ENC::')) {
    rawCats = await decryptField(uid, rawCats);
  }

  const { defaultCats, defaultPrices } = getDefaultsByMode(targetMode);
  return { cats: rawCats || defaultCats, prices: rawPrices || defaultPrices };
}

// ── V121: 저장 보장 ────────────────────────────────────────────────────────────
// 2026-10-12·13 허페이: 단가표에 입력한 내용이 다음 날 통째로 없어졌다.
// 예전 저장 방식의 구멍:
//   · 저장이 실패해도(VPN 끊김·보안 확인 실패 등) 콘솔에만 찍히고 화면은 "된 것처럼" 보였다
//   · 저장이 끝나기 전에 창을 닫으면 그 내용은 그대로 사라졌다
//   · 불러오기가 5초 넘게 걸리면 예시 데이터를 보여주고, 그 위에 입력한 건 저장 자체를 안 했다
//   · 화면을 불러온 뒤엔 다시 읽지 않아서, 오래 열어 둔 다른 창이 옛 목록을 통째로 덮어쓸 수 있었다
// 고친 방식:
//   · 모든 저장은 대기열을 거친다. 실패하면 자동으로 다시 보내고(3초→최대 30초 간격, 인터넷이 돌아오면 즉시)
//     화면 위에 "저장 안 됨"을 띄운다. 저장 안 된 게 있으면 창을 닫을 때 브라우저가 한 번 묻는다
//   · 저장 안 된 내용은 이 기기에도 따로 적어 둔다 → 창이 닫혀도 다음에 열면 "복원할까요?"로 되살린다
//   · 불러오기가 느리면 예시 데이터 대신 "불러오는 중"을 계속 보여주고, 실패하면 "다시 시도" 버튼을 띄운다
//   · 불러온 뒤에도 서버 문서를 실시간으로 지켜봐서, 다른 창·기기에서 바뀐 내용이 이 화면에 바로 들어온다
const BACKUP_KEY      = 'semfit_unsaved_v1';        // 지금 저장 대기 중인 것 (uid → { savedAt, fields })
const BACKUP_PREV_KEY = 'semfit_unsaved_prev_v1';   // 지난번 창에서 못 보낸 것 — 복원/버리기 결정 대기
const SAVE_TIMEOUT_MS = 20000;
const LOAD_SLOW_MS    = 5000;
const LOAD_GIVEUP_MS  = 30000;
// V148: 중국 와이파이(jjggfood.com)에서는 연결이 자주 끊겼다 붙는다. 한 번 실패로 바로 '불러오지 못했어요'를
//       띄우지 않고, 연결을 새로 붙인 뒤 몇 번 더 조용히 다시 불러온다 (기다리는 시간, ms)
// V149 오늘의 작업 — 저장한 내역은 최근 이만큼만 (매장 문서 하나에 다 들어가므로)
export const WORK_HISTORY_MAX = 120;
export const MEETINGS_MAX = 60;   // V156 회의 기록 (매장 문서 1MB 한도를 같이 쓴다)
const normWorkToday = (v) => (v && typeof v === 'object' && !Array.isArray(v))
  ? { date: String(v.date || ''), items: Array.isArray(v.items) ? v.items : [] }
  : { date: '', items: [] };
const workerName = (u) => String(u?.displayName || String(u?.email || '').split('@')[0] || '').slice(0, 30);

const LOAD_AUTO_RETRY = IS_CHINA_MODE ? [2000, 5000, 10000] : [2000];
const isTransientLoadError = (code) => /unavailable|deadline|timeout|offline|internal|unknown|network|aborted|failed-precondition|load-failed|fetch/i.test(String(code || ''));

// V148: 끊긴 연결을 버리고 새로 붙인다 (Firestore가 '오프라인'으로 판단해 한참 기다리는 상태를 푼다)
async function resetFirestoreNetwork() {
  try { await disableNetwork(db); } catch (e) { /* 무시 */ }
  try { await enableNetwork(db); } catch (e) { /* 무시 */ }
}

function readStore(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch (e) { return {}; }
}
function writeStore(key, obj) {
  try {
    if (obj && Object.keys(obj).length) localStorage.setItem(key, JSON.stringify(obj));
    else localStorage.removeItem(key);
    return true;
  } catch (e) {
    console.warn('[save] 기기 백업 실패:', e?.message);
    return false;
  }
}

function withTimeout(promise, ms) {
  let t;
  return Promise.race([
    promise,
    new Promise((_, reject) => { t = setTimeout(() => reject(Object.assign(new Error('save timeout'), { code: 'timeout' })), ms); }),
  ]).finally(() => clearTimeout(t));
}

const sameJson = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; }
};

// V50: ownerUidOverride 인자로 매니저 모드 지원. 없으면 본인 uid 사용.
export function useFirestore(user, ownerUidOverride) {
  const ownerUid = ownerUidOverride || user?.uid || null;
  const [categories,   setCategories]   = useState(null);
  const [prices,       setPrices]       = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [profitLoss,   setProfitLoss]   = useState({});
  const [taxData,      setTaxData]      = useState({});
  const [inventory,    setInventory]    = useState({});
  const [projects,     setProjects]     = useState([]);
  const [employees,        setEmployees]        = useState([]);
  const [schedules,        setSchedules]        = useState({});
  const [payrollSettings,  setPayrollSettings]  = useState({});
  const [menuRecipeMap, setMenuRecipeMap] = useState({});
  // V149 오늘의 작업: 오늘 목록 · 체크(누가·언제) · 저장한 날짜별 내역
  const [workToday,   setWorkToday]   = useState({ date: '', items: [] });
  const [workChecks,  setWorkChecks]  = useState({});
  const [workHistory, setWorkHistory] = useState([]);
  const [meetings,    setMeetings]    = useState([]);   // V156 회의
  const [industry,     setIndustry]     = useState('');
  const [loading,      setLoading]      = useState(true);
  const [loadedMode,   setLoadedMode]   = useState(null);
  // V121
  const [slowLoad,     setSlowLoad]     = useState(false);
  const [loadError,    setLoadError]    = useState('');
  const [reloadKey,    setReloadKey]    = useState(0);
  const [loadRetry,    setLoadRetry]    = useState(0);   // V148 자동으로 다시 불러온 횟수 (화면 안내용)
  const loadAttemptRef = useRef({ key: '', n: 0 });
  const [saveState,    setSaveState]    = useState({ pending: 0, failing: false, lastSavedAt: 0, lastError: '' });
  const [unsavedBackup, setUnsavedBackup] = useState(null);   // { uid, savedAt, fields }

  // ── 저장 대기열 ──────────────────────────────────────────────────────────────
  const pendingRef   = useRef(new Map());   // `${uid}|${field}` → { uid, field, value, encrypt, at }
  const touchedRef   = useRef(new Set());   // 이번 창에서 저장한 적 있는 uid (기기 백업 정리용)
  const flushingRef  = useRef(false);
  const againRef     = useRef(false);
  const retryTimer   = useRef(null);
  const debounceTimer = useRef(null);
  const backoffRef   = useRef(0);
  const failingRef   = useRef(false);
  const lastSavedRef = useRef(0);
  const lastErrorRef = useRef('');

  const publish = () => setSaveState({
    pending: pendingRef.current.size, failing: failingRef.current,
    lastSavedAt: lastSavedRef.current, lastError: lastErrorRef.current,
  });

  // 저장 대기 중인 값을 이 기기에도 적어 둔다 (창이 닫혀도 다음에 되살릴 수 있게)
  const syncBackup = () => {
    const all = readStore(BACKUP_KEY);
    const byUid = {};
    for (const e of pendingRef.current.values()) {
      if (!byUid[e.uid]) byUid[e.uid] = { savedAt: 0, fields: {} };
      byUid[e.uid].fields[e.field] = e.value;
      byUid[e.uid].savedAt = Math.max(byUid[e.uid].savedAt, e.at);
    }
    for (const uid of touchedRef.current) {
      if (byUid[uid]) all[uid] = byUid[uid]; else delete all[uid];
    }
    writeStore(BACKUP_KEY, all);
  };

  const flush = async () => {
    if (flushingRef.current) { againRef.current = true; return; }
    clearTimeout(retryTimer.current);
    if (pendingRef.current.size === 0) { failingRef.current = false; publish(); return; }
    flushingRef.current = true;
    const byUid = new Map();
    for (const e of pendingRef.current.values()) {
      if (!byUid.has(e.uid)) byUid.set(e.uid, []);
      byUid.get(e.uid).push(e);
    }
    let ok = true;
    for (const [uid, list] of byUid) {
      try {
        const patch = {};
        for (const e of list) patch[e.field] = e.encrypt ? await encryptField(uid, e.value) : e.value;
        await withTimeout(setDoc(doc(db, 'users', uid), patch, { merge: true }), SAVE_TIMEOUT_MS);
        // 보내는 사이 또 고친 칸은 남겨 두고 다음 차례에 보낸다
        for (const e of list) {
          const key = `${e.uid}|${e.field}`;
          if (pendingRef.current.get(key) === e) pendingRef.current.delete(key);
        }
        lastSavedRef.current = Date.now();
      } catch (err) {
        ok = false;
        lastErrorRef.current = String(err?.code || err?.message || err);
        console.error('[save] 저장 실패 — 다시 시도합니다:', lastErrorRef.current);
      }
    }
    flushingRef.current = false;
    failingRef.current = !ok;
    if (ok) { backoffRef.current = 0; lastErrorRef.current = ''; }
    syncBackup();
    publish();
    if (!ok) {
      backoffRef.current = Math.min(30000, backoffRef.current ? backoffRef.current * 2 : 3000);
      retryTimer.current = setTimeout(flush, backoffRef.current);
    } else if (againRef.current || pendingRef.current.size) {
      againRef.current = false;
      if (pendingRef.current.size) setTimeout(flush, 0);
    }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  /** 서버 문서의 칸들을 저장 대기열에 넣는다. encryptFields: 암호화해서 보낼 칸 이름 */
  const queueSave = (uid, fields, encryptFields) => {
    if (!uid) return;
    touchedRef.current.add(uid);
    const now = Date.now();
    for (const field of Object.keys(fields)) {
      pendingRef.current.set(`${uid}|${field}`, {
        uid, field, value: fields[field], at: now,
        encrypt: !!(encryptFields && encryptFields.includes(field)),
      });
    }
    syncBackup();
    publish();
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => flushRef.current(), 400);   // 연속 입력은 모아서 보낸다
  };

  // 인터넷이 돌아오면 바로 다시 보낸다 / 저장 안 된 게 있으면 창 닫을 때 묻는다
  useEffect(() => {
    const onOnline = () => flushRef.current();
    const onBeforeUnload = (e) => {
      if (pendingRef.current.size === 0) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  // ── 불러오기 ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;

    // V148: 매장(uid)이 바뀌면 자동 재시도 횟수를 처음부터
    const attemptKey = `${user.uid}|${ownerUid}`;
    if (loadAttemptRef.current.key !== attemptKey) loadAttemptRef.current = { key: attemptKey, n: 0 };

    const load = async () => {
      setLoading(true);
      setLoadError('');
      setSlowLoad(loadAttemptRef.current.n > 0);
      // V118: 매장(ownerUid)을 바꾸면 새 매장 데이터가 올 때까지 저장을 막는다.
      setLoadedMode(null);
      // V121: 느려도 예시 데이터로 바꿔치기하지 않는다 — 그 위에 입력한 건 저장이 안 됐다
      const slowTimer = setTimeout(() => { if (!cancelled) setSlowLoad(true); }, LOAD_SLOW_MS);
      const giveUpTimer = setTimeout(() => {
        if (!cancelled) setLoadError('timeout');
      }, LOAD_GIVEUP_MS);

      try {
        const ref  = doc(db, 'users', ownerUid);
        const snap = await getDoc(ref);
        if (cancelled) return;

        const storedMode = localStorage.getItem('ddabak_mode') || '';

        if (snap.exists()) {
          const data = snap.data();
          const ind  = data.industry || '';
          const industryMode = getIndustryMode(ind);
          const targetMode   = storedMode || industryMode || 'food';

          setIndustry(ind);
          setOrderHistory(data.orderHistory || []);
          setProfitLoss(data.profitLoss     || {});
          setTaxData(data.taxData           || {});
          setInventory(data.inventory       || {});
          setProjects(data.projects         || []);
          setEmployees(data.employees             || []);
          setSchedules(data.schedules             || {});
          setPayrollSettings(data.payrollSettings || {});
          setMenuRecipeMap(data.menuRecipeMap || {});
          setWorkToday(normWorkToday(data.workToday));
          setWorkChecks(data.workChecks && typeof data.workChecks === 'object' ? data.workChecks : {});
          setWorkHistory(Array.isArray(data.workHistory) ? data.workHistory : []);
          setMeetings(Array.isArray(data.meetings) ? data.meetings : []);

          const { cats, prices: priceData } =
            await fetchModeData(ownerUid, data, targetMode, industryMode);
          if (cancelled) return;
          setCategories(cats);
          setPrices(priceData);
          setLoadedMode(targetMode);

          if (data.categories && typeof data.categories !== 'string') {
            const enc = await encryptField(ownerUid, data.categories);
            await setDoc(ref, { categories: enc }, { merge: true });
          }
        } else {
          let ind = '';
          try {
            const freshSnap = await getDoc(ref);
            ind = freshSnap.exists() ? (freshSnap.data().industry || '') : '';
          } catch (_) {}
          const industryMode = getIndustryMode(ind);
          const targetMode   = storedMode || industryMode || 'food';
          const { defaultCats, defaultPrices } = getDefaultsByMode(targetMode);

          const encCats = await encryptField(ownerUid, defaultCats);
          const initDoc = { orderHistory: [], profitLoss: {}, projects: [] };
          initDoc[catsField(targetMode)]   = encCats;
          initDoc[pricesField(targetMode)] = defaultPrices;
          await setDoc(ref, initDoc, { merge: true });
          if (cancelled) return;

          setIndustry(ind);
          setCategories(defaultCats);
          setPrices(defaultPrices);
          setOrderHistory([]);
          setProjects([]);
          setWorkToday({ date: '', items: [] });
          setWorkChecks({});
          setWorkHistory([]);
          setMeetings([]);
          setLoadedMode(targetMode);

          const month = new Date().toISOString().slice(0, 7);
          const statsUpdate = { totalUsers: increment(1) };
          statsUpdate['newUsers_' + month] = increment(1);
          await setDoc(doc(db, 'stats', 'global'), statsUpdate, { merge: true });
        }

        // 지난번 창에서 못 보낸 내용이 이 기기에 남아 있으면 → 복원할지 물어본다
        const cur = readStore(BACKUP_KEY);
        const prev = readStore(BACKUP_PREV_KEY);
        // (1분 안쪽 것은 지금 다른 창이 보내는 중일 수 있어 건드리지 않는다)
        if (cur[ownerUid] && !touchedRef.current.has(ownerUid) && Date.now() - (cur[ownerUid].savedAt || 0) > 60000) {
          prev[ownerUid] = cur[ownerUid];
          delete cur[ownerUid];
          writeStore(BACKUP_KEY, cur);
          writeStore(BACKUP_PREV_KEY, prev);
        }
        const leftover = prev[ownerUid];
        setUnsavedBackup(leftover && leftover.fields && Object.keys(leftover.fields).length
          ? { uid: ownerUid, savedAt: leftover.savedAt || 0, fields: leftover.fields }
          : null);

        clearTimeout(slowTimer);
        clearTimeout(giveUpTimer);
        loadAttemptRef.current.n = 0;
        setLoadRetry(0);
        setSlowLoad(false);
        setLoadError('');
        setLoading(false);
      } catch (e) {
        clearTimeout(slowTimer);
        clearTimeout(giveUpTimer);
        if (cancelled) return;
        console.error('Firestore load error:', e);
        const code = String(e?.code || e?.message || 'load-failed');
        // V148: 연결 문제면 연결을 새로 붙이고 조용히 다시 불러온다 (몇 번까지)
        const n = loadAttemptRef.current.n;
        if (isTransientLoadError(code) && n < LOAD_AUTO_RETRY.length) {
          loadAttemptRef.current.n = n + 1;
          setLoadRetry(n + 1);
          setSlowLoad(true);
          if (IS_CHINA_MODE) await resetFirestoreNetwork();
          if (cancelled) return;
          setTimeout(() => { if (!cancelled) setReloadKey(k => k + 1); }, LOAD_AUTO_RETRY[n]);
          return;
        }
        // 예시 데이터를 보여주지 않는다 — 다시 시도 화면 (loading 유지)
        setLoadError(code);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user, ownerUid, reloadKey]);

  // V148: '다시 시도' — 자동 재시도 횟수를 처음부터, 중국 모드는 연결도 새로 붙인다
  const reload = async () => {
    loadAttemptRef.current.n = 0;
    setLoadRetry(0);
    if (IS_CHINA_MODE) await resetFirestoreNetwork();
    setReloadKey(k => k + 1);
  };

  // ── 실시간 동기화: 다른 창·기기에서 바뀐 내용을 이 화면에 반영 ─────────────────
  const currentRef = useRef({});
  currentRef.current = {
    __mode: loadedMode,
    ...(loadedMode ? { [pricesField(loadedMode)]: prices, [catsField(loadedMode)]: categories } : {}),
    orderHistory, profitLoss, taxData, inventory, projects,
    employees, schedules, payrollSettings, menuRecipeMap,
    workToday, workChecks, workHistory, meetings,
  };
  useEffect(() => {
    if (!user || !ownerUid || !loadedMode || loading) return undefined;
    const uid = ownerUid;
    const mode = loadedMode;
    const setters = {
      [pricesField(mode)]: setPrices,
      [catsField(mode)]: setCategories,
      orderHistory: setOrderHistory, profitLoss: setProfitLoss, taxData: setTaxData,
      inventory: setInventory, projects: setProjects, employees: setEmployees,
      schedules: setSchedules, payrollSettings: setPayrollSettings, menuRecipeMap: setMenuRecipeMap,
      workToday: (v) => setWorkToday(normWorkToday(v)),
      workChecks: (v) => setWorkChecks(v && typeof v === 'object' ? v : {}),
      workHistory: (v) => setWorkHistory(Array.isArray(v) ? v : []),
      meetings: (v) => setMeetings(Array.isArray(v) ? v : []),
    };
    const seen = {};
    let alive = true;
    const unsub = onSnapshot(doc(db, 'users', uid), async (snap) => {
      // 이 화면에서 보낸 게 아직 서버에 안 닿은 상태의 스냅샷은 건너뛴다
      if (!alive || !snap.exists() || snap.metadata.hasPendingWrites) return;
      const data = snap.data();
      for (const field of Object.keys(setters)) {
        if (!(field in data)) continue;
        if (pendingRef.current.has(`${uid}|${field}`)) continue;   // 이 화면에서 고친 게 우선
        let val = data[field];
        const raw = typeof val === 'string' ? val : JSON.stringify(val);
        if (seen[field] === raw) continue;
        seen[field] = raw;
        if (field === catsField(mode) && typeof val === 'string' && val.startsWith('ENC::')) {
          try { val = await decryptField(uid, val); } catch (e) { continue; }
        }
        if (!alive || currentRef.current.__mode !== mode) return;
        if (pendingRef.current.has(`${uid}|${field}`)) continue;
        if (sameJson(currentRef.current[field], val)) continue;     // 내가 보낸 저장이 돌아온 것
        console.info('[sync] 다른 화면에서 바뀐 내용 반영:', field);
        setters[field](val);
      }
    }, (err) => console.warn('[sync] 실시간 연결 오류:', err?.code || err?.message));
    return () => { alive = false; unsub(); };
  }, [user, ownerUid, loadedMode, loading]);

  // ── 지난번 못 보낸 내용 복원/버리기 ───────────────────────────────────────────
  const restoreUnsaved = () => {
    const b = unsavedBackup;
    if (!b) return;
    const enc = [];
    for (const field of Object.keys(b.fields)) {
      const v = b.fields[field];
      if (loadedMode && field === pricesField(loadedMode)) setPrices(v);
      else if (loadedMode && field === catsField(loadedMode)) setCategories(v);
      else if (field === 'orderHistory') setOrderHistory(v);
      else if (field === 'profitLoss') setProfitLoss(v);
      else if (field === 'taxData') setTaxData(v);
      else if (field === 'inventory') setInventory(v);
      else if (field === 'projects') setProjects(v);
      else if (field === 'employees') setEmployees(v);
      else if (field === 'schedules') setSchedules(v);
      else if (field === 'payrollSettings') setPayrollSettings(v);
      else if (field === 'menuRecipeMap') setMenuRecipeMap(v);
      else if (field === 'workToday') setWorkToday(normWorkToday(v));
      else if (field === 'workChecks') setWorkChecks(prev => ({ ...prev, ...(v || {}) }));   // 체크는 칸별로 합친다
      else if (field === 'workHistory') setWorkHistory(Array.isArray(v) ? v : []);
      else if (field === 'meetings') setMeetings(Array.isArray(v) ? v : []);
      if (field.startsWith('cats_')) enc.push(field);
    }
    queueSave(b.uid, b.fields, enc);
    discardUnsaved();
  };
  const discardUnsaved = () => {
    if (unsavedBackup) {
      const prev = readStore(BACKUP_PREV_KEY);
      delete prev[unsavedBackup.uid];
      writeStore(BACKUP_PREV_KEY, prev);
    }
    setUnsavedBackup(null);
  };

  const switchMode = async (targetMode) => {
    if (!user || loadedMode === targetMode) return;
    try {
      const ref  = doc(db, 'users', ownerUid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      const data = snap.data();
      const industryMode = getIndustryMode(data.industry || '');
      const { cats, prices: priceData } =
        await fetchModeData(ownerUid, data, targetMode, industryMode);
      setCategories(cats);
      setPrices(priceData);
      setLoadedMode(targetMode);
    } catch (e) {
      console.error('mode switch error:', e);
    }
  };

  const updateCategories = async (next) => {
    setCategories(next);
    if (!user || !loadedMode) { console.warn('[save] 아직 불러오는 중이라 레시피 저장을 건너뜀'); return; }
    const f = catsField(loadedMode);
    queueSave(ownerUid, { [f]: next }, [f]);
  };

  const updatePrices = async (next) => {
    setPrices(next);
    if (!user || !loadedMode) { console.warn('[save] 아직 불러오는 중이라 단가표 저장을 건너뜀'); return; }
    queueSave(ownerUid, { [pricesField(loadedMode)]: next });
  };

  const saveStats = async (entry) => {
    try {
      const userSnap = await getDoc(doc(db, 'users', ownerUid));
      const ind = userSnap.exists() ? (userSnap.data().industry || 'etc') : 'etc';
      const month = entry.date.slice(0, 7);

      const monthUpdate = { month, orderCount: increment(1), totalItemCount: increment(entry.items.length) };
      monthUpdate['industry_' + ind] = increment(1);
      await setDoc(doc(db, 'stats', 'month_' + month), monthUpdate, { merge: true });

      await Promise.all(entry.items.map(item => {
        const safeName = item.name.replace(/\//g, '_');
        const ingUpdate = {
          name: item.name,
          orderCount: increment(1),
          totalQty:  increment(Number(item.qty) || 0),
          lastPrice: item.price || 0,
          lastSeen:  entry.date,
        };
        ingUpdate['month_'    + month] = increment(1);
        ingUpdate['industry_' + ind]   = increment(1);
        return setDoc(doc(db, 'stats', 'ing_' + safeName), ingUpdate, { merge: true });
      }));

      const activeKey  = 'active_' + ownerUid + '_' + month;
      const activeRef  = doc(db, 'stats', activeKey);
      const activeSnap = await getDoc(activeRef);
      if (!activeSnap.exists()) {
        await setDoc(activeRef, { uid_month: activeKey });
        const globalUpdate = {};
        globalUpdate['activeUsers_' + month] = increment(1);
        await setDoc(doc(db, 'stats', 'global'), globalUpdate, { merge: true });
      }
    } catch (e) { console.warn('stats save skipped:', e); }
  };


  // V138: 발주처별로 따로 저장 — 여러 개를 한 번에 받을 수 있다 (하나씩 연달아 부르면 서로 덮어씀)
  const saveOrderHistory = async (entry) => {
    const list = (Array.isArray(entry) ? entry : [entry]).filter(Boolean);
    if (!list.length) return;
    const next = [...list, ...orderHistory].slice(0, 365);
    setOrderHistory(next);
    if (!user) return;
    queueSave(ownerUid, { orderHistory: next });
    list.forEach(e => saveStats(e));
  };

  // V138 발주 내역 고치기 (수량 정정) — 같은 id를 새 내용으로 바꾼다
  const updateOrderHistory = async (entry) => {
    if (!entry || !entry.id) return;
    const next = orderHistory.map(h => (h.id === entry.id ? entry : h));
    setOrderHistory(next);
    if (!user) return;
    queueSave(ownerUid, { orderHistory: next });
  };

  // V158 입고: 발주서 여러 장의 상태(receive)를 한 번에 바꾼다 (updateOrderHistory를 연달아 부르면 서로 덮어씀)
  const updateOrderHistoryMany = async (entries) => {
    const map = new Map((entries || []).filter(e => e && e.id != null).map(e => [e.id, e]));
    if (!map.size) return;
    const next = orderHistory.map(h => (map.has(h.id) ? map.get(h.id) : h));
    setOrderHistory(next);
    if (!user) return;
    queueSave(ownerUid, { orderHistory: next });
  };

  const deleteOrderHistory = async (id) => {
    const next = orderHistory.filter(h => h.id !== id);
    setOrderHistory(next);
    if (!user) return;
    queueSave(ownerUid, { orderHistory: next });
  };

  // V137 발주 내역 합치기 — 합친 발주서를 넣고 원래 발주서들을 한 번에 지운다
  // (saveOrderHistory·deleteOrderHistory를 연달아 부르면 둘 다 같은 옛 목록에서 시작해서 서로 덮어씀)
  const mergeOrderHistory = async (ids, merged) => {
    const drop = new Set(ids || []);
    const next = [merged, ...orderHistory.filter(h => !drop.has(h.id))].slice(0, 365);
    setOrderHistory(next);
    if (!user) return;
    queueSave(ownerUid, { orderHistory: next });
  };

  const updateProfitLoss = async (next) => {
    setProfitLoss(next);
    if (!user) return;
    queueSave(ownerUid, { profitLoss: next });
  };

  const updateTaxData = async (next) => {
    setTaxData(next);
    if (!user) return;
    queueSave(ownerUid, { taxData: next });
  };

  const updateInventory = async (next) => {
    setInventory(next);
    if (!user) return;
    queueSave(ownerUid, { inventory: next });
  };

  const updateEmployees = async (next) => {
    setEmployees(next);
    if (!user) return;
    queueSave(ownerUid, { employees: next });
  };

  const updateSchedules = async (next) => {
    setSchedules(next);
    if (!user) return;
    queueSave(ownerUid, { schedules: next });
  };

  const updatePayrollSettings = async (next) => {
    setPayrollSettings(next);
    if (!user) return;
    queueSave(ownerUid, { payrollSettings: next });
  };

  const updateMenuRecipeMap = async (next) => {
    setMenuRecipeMap(next);
    if (!user) return;
    queueSave(ownerUid, { menuRecipeMap: next });
  };

  // ── V149 오늘의 작업 ──────────────────────────────────────────────────────────
  const updateWorkToday = async (next) => {
    const v = normWorkToday(next);
    setWorkToday(v);
    if (!user) return;
    queueSave(ownerUid, { workToday: v });
  };

  // 체크는 바꾼 칸만 보낸다 → 직원 여럿이 동시에 체크해도 서버에서 합쳐져서 서로 안 지워진다
  // (Firestore merge 저장은 맵 안쪽 칸끼리 합친다. 아직 못 보낸 체크가 있으면 그것까지 같이)
  const setWorkCheck = async (itemId, done) => {
    if (!itemId) return;
    const entry = { done: !!done, at: Date.now(), by: workerName(user) };
    setWorkChecks(prev => ({ ...prev, [itemId]: entry }));
    if (!user) return;
    const pend = pendingRef.current.get(`${ownerUid}|workChecks`);
    queueSave(ownerUid, { workChecks: { ...((pend && pend.value) || {}), [itemId]: entry } });
  };

  // 발주서처럼 저장 — 맨 앞에 넣는다. clear면 오늘 목록·체크를 비우고 새로 시작
  // V155: keepItems — 안 끝난 항목(기한이 남은 회의 주제 등)은 새 목록에 남긴다 (체크는 새로 시작)
  const saveWorkHistory = async (entry, { clear = true, nextDate = '', keepItems = null } = {}) => {
    if (!entry) return;
    const next = [entry, ...workHistory.filter(h => h.id !== entry.id)].slice(0, WORK_HISTORY_MAX);
    setWorkHistory(next);
    const fields = { workHistory: next };
    if (clear) {
      const empty = { date: nextDate || '', items: Array.isArray(keepItems) ? keepItems : [] };
      setWorkToday(empty);
      setWorkChecks({});
      fields.workToday = empty;
      fields.workChecks = {};   // 빈 맵으로 저장하면 칸 전체가 비워진다
    }
    if (!user) return;
    queueSave(ownerUid, fields);
  };

  // V156: 오늘의 작업 · 체크 · 내역 · 회의를 한 번에 (넘기기·완료 저장·회의 저장)
  //   workChecks는 통째로 바꿀 때만 빈 맵 {}을 보낸다 (빈 맵 = 칸 전체 교체, 아니면 칸끼리 합쳐짐)
  const commitWork = async (patch = {}) => {
    const fields = {};
    if (patch.workToday) { const v = normWorkToday(patch.workToday); setWorkToday(v); fields.workToday = v; }
    if (patch.workChecks && typeof patch.workChecks === 'object') { setWorkChecks(patch.workChecks); fields.workChecks = patch.workChecks; }
    if (Array.isArray(patch.workHistory)) { const v = patch.workHistory.slice(0, WORK_HISTORY_MAX); setWorkHistory(v); fields.workHistory = v; }
    if (Array.isArray(patch.meetings)) { const v = patch.meetings.slice(0, MEETINGS_MAX); setMeetings(v); fields.meetings = v; }
    if (!user || !Object.keys(fields).length) return;
    queueSave(ownerUid, fields);
  };

  const deleteWorkHistory = async (id) => {
    const next = workHistory.filter(h => h.id !== id);
    setWorkHistory(next);
    if (!user) return;
    queueSave(ownerUid, { workHistory: next });
  };

  const saveProject = async (proj) => {
    const next = projects.some(p => p.id === proj.id)
      ? projects.map(p => p.id === proj.id ? proj : p)
      : [proj, ...projects];
    setProjects(next);
    if (!user) return;
    queueSave(ownerUid, { projects: next });
  };

  const deleteProject = async (id) => {
    const next = projects.filter(p => p.id !== id);
    setProjects(next);
    if (!user) return;
    queueSave(ownerUid, { projects: next });
  };

  return {
    categories, prices, orderHistory, profitLoss, taxData, inventory,
    industry, loading, loadedMode, projects,
    employees, schedules, payrollSettings,
    updateCategories, updatePrices, saveOrderHistory, deleteOrderHistory, mergeOrderHistory, updateOrderHistory, updateOrderHistoryMany,
    updateProfitLoss, updateTaxData, updateInventory, switchMode,
    saveProject, deleteProject,
    updateEmployees, updateSchedules, updatePayrollSettings,
    menuRecipeMap, updateMenuRecipeMap,
    // V149 오늘의 작업
    workToday, workChecks, workHistory, updateWorkToday, setWorkCheck, saveWorkHistory, deleteWorkHistory,
    meetings, commitWork,   // V156 회의 · 넘기기
    // V121 저장 보장
    slowLoad, loadError, reload, loadRetry, loadRetryMax: LOAD_AUTO_RETRY.length,   // V148
    saveState, retrySaves: () => flushRef.current(),
    unsavedBackup, restoreUnsaved, discardUnsaved,
  };
}
